import { DOCUMENT } from '@angular/core';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription, timer } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { AuthService } from '@core/services/authentication/auth.service';
import { LoadingService } from '@core/services/loading/loading.service';
import { SessionService } from '@core/services/session/session.service';
import { isRenaultApp } from '@core/utils/apps.utils';
import { InactivityModalComponent } from '@shared/components/inactivity-modal/inactivity-modal.component';

/** MFS-350 Accesibilidad
 * Servicio que gestiona la inactividad del usuario y muestra un modal de advertencia antes de cerrar la sesión.

 */
@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  /** Tiempo de inactividad antes de mostrar el modal (2 minutos, temporal). */
  readonly INACTIVITY_TIMEOUT_MS = 2 * 60 * 1000;

  /** Duración de la cuenta atrás en el modal (120 segundos de aviso). */
  readonly WARNING_TIMEOUT_SECONDS = 2 * 60;

  private destroy$ = new Subject<void>();
  private inactivityTimerSub: Subscription | null = null;
  private warningTimerSub: Subscription | null = null;
  private modalRef: BsModalRef | null = null;
  private readonly activityHandler = this.onUserActivity.bind(this);
  // Indica si la vigilancia de inactividad está activa (usuario logeado dentro del área privada).
  private isSessionActive = false;
  private isLoadingActive = false;
  // Elemento que tenía el foco antes de abrir el modal, para restaurarlo al cerrar.
  private lastFocusedElement: HTMLElement | null = null;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private readonly modalService: BsModalService,
    private readonly sessionService: SessionService,
    private readonly authService: AuthService,
    private readonly loadingService: LoadingService,
  ) {
    this.subscribeToRouterEvents();
    this.subscribeToLoadingState();
    this.registerActivityListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resetInactivityTimer();
    this.clearWarningTimer();
    this.closeModal();
    this.removeActivityListeners();
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        const nav = event as NavigationEnd;
        const isPrivateAreaRoute = nav.urlAfterRedirects.includes('private-area');
        const isLogged = this.authService.isLogged();
        const isWebView = isRenaultApp();

        //Comprobamos antes si está logeado y en el área privada, y no estamos en webview.
        this.isSessionActive = !isWebView && isPrivateAreaRoute && isLogged;

        if (this.isSessionActive) {
          this.restartInactivityTimer();
        } else {
          this.resetInactivityTimer();
          this.clearWarningTimer();
          this.closeModal();
        }
      });
  }

  private subscribeToLoadingState(): void {
    this.loadingService.showLoading$.pipe(takeUntil(this.destroy$)).subscribe((isLoading) => {
      this.isLoadingActive = isLoading;

      if (isLoading) {
        this.resetInactivityTimer();
        this.clearWarningTimer();
        this.closeModal();
        return;
      }

      if (this.isSessionActive && !this.modalRef) {
        this.restartInactivityTimer();
      }
    });
  }

  /**
   * Registra los listeners globales.
   */
  private registerActivityListeners(): void {
    this.document.addEventListener('mousemove', this.activityHandler, true);
    this.document.addEventListener('mousedown', this.activityHandler, true);
    this.document.addEventListener('keydown', this.activityHandler, true);
    this.document.addEventListener('touchstart', this.activityHandler, true);
    this.document.addEventListener('wheel', this.activityHandler, true);
    this.document.addEventListener('scroll', this.activityHandler, true);
  }
  /**
   * Elimina los listeners globales.
   */
  private removeActivityListeners(): void {
    this.document.removeEventListener('mousemove', this.activityHandler, true);
    this.document.removeEventListener('mousedown', this.activityHandler, true);
    this.document.removeEventListener('keydown', this.activityHandler, true);
    this.document.removeEventListener('touchstart', this.activityHandler, true);
    this.document.removeEventListener('wheel', this.activityHandler, true);
    this.document.removeEventListener('scroll', this.activityHandler, true);
  }

  private onUserActivity(): void {
    // Si hay un modal de advertencia abierto, sólo se limita a
    // interacciones dentro del modal, por lo que no se reinicia automáticamente.

    // Si no hay sesión activa (por ejemplo en /login), la actividad
    // del usuario no debe iniciar ni reiniciar la vigilancia.
    if (!this.isSessionActive || this.modalRef) {
      return;
    }

    this.restartInactivityTimer();
  }

  private restartInactivityTimer(): void {
    // Reinicia el temporizador de inactividad cada vez que se detecta actividad del usuario.
    this.resetInactivityTimer();

    this.inactivityTimerSub = timer(this.INACTIVITY_TIMEOUT_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.showInactivityAlertModal());
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimerSub) {
      this.inactivityTimerSub.unsubscribe();
      this.inactivityTimerSub = null;
    }
  }

  private showInactivityAlertModal(): void {
    // Sólo mostramos el modal si hay sesión activa dentro del área privada.
    if (!this.isSessionActive || this.modalRef || this.isLoadingActive) {
      return;
    }

    // Guardamos el elemento con foco antes de abrir el modal.
    this.lastFocusedElement = this.document.activeElement as HTMLElement | null;

    // Apertura del modal con estado inicial de la cuenta atrás.
    this.modalRef = this.modalService.show(InactivityModalComponent, {
      backdrop: 'static',
      keyboard: false,
      ignoreBackdropClick: true,
      initialState: {
        totalSeconds: this.WARNING_TIMEOUT_SECONDS,
        remainingSeconds: this.WARNING_TIMEOUT_SECONDS,
      },
      ariaLabelledBy: 'inactivity-dialog-title',
      ariaDescribedby: 'inactivity-dialog-description',
    });

    const component = this.modalRef.content as InactivityModalComponent | null;
    if (component) {
      this.initializeModalEvents(component);
      this.startInactivityCountdown(component);
    }
  }

  private initializeModalEvents(component: InactivityModalComponent): void {
    component.continueSession.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearWarningTimer();
      this.closeModal();
      this.restartInactivityTimer();
    });

    component.logoutSession.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.logoutOnInactivity();
    });
  }

  private startInactivityCountdown(component: InactivityModalComponent): void {
    // Arranca el descenso un segundo después para respetar el 02:00 inicial ya pintado.
    this.clearWarningTimer();

    const startedAt = Date.now();
    this.warningTimerSub = timer(1000, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
        const remaining = this.WARNING_TIMEOUT_SECONDS - elapsedSeconds;

        component.updateRemainingSeconds(remaining > 0 ? remaining : 0);

        if (remaining <= 0) {
          this.logoutOnInactivity();
        }
      });
  }

  private clearWarningTimer(): void {
    if (this.warningTimerSub) {
      this.warningTimerSub.unsubscribe();
      this.warningTimerSub = null;
    }
  }

  private closeModal(): void {
    const modalRef = this.modalRef;

    if (!modalRef) {
      return;
    }

    this.modalRef = null;
    this.modalService.onHidden.pipe(take(1)).subscribe(() => {
      this.restoreFocusAfterModal();
      this.restoreUnderlyingModalScroll();
    });
    modalRef.hide();
  }

  private restoreUnderlyingModalScroll(): void {
    if (this.modalService.getModalsCount() > 0) {
      this.document.body.classList.add('modal-open');
    }
  }

  private logoutOnInactivity(): void {
    this.clearWarningTimer();
    this.closeModal();

    this.sessionService.logout().subscribe();
  }

  /**
   * Restaura el foco al elemento que lo tenía antes de abrir el modal,
   * siempre que siga presente en el documento y sea enfocables.
   */
  private restoreFocusAfterModal(): void {
    const target = this.lastFocusedElement;

    if (!target) {
      return;
    }

    this.lastFocusedElement = null;

    try {
      if (
        target instanceof HTMLElement &&
        this.document.contains(target) &&
        typeof target.focus === 'function'
      ) {
        target.focus();
      }
    } catch {
      // Si restaurar el foco falla, no hacemos nada para evitar errores de ejecución.
    }
  }
}
